import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, Newspaper, FileText, LogOut, Home, BarChart3, SlidersHorizontal, Cable, Layers, X, Building2, LayoutTemplate, BookOpen, TrendingUp } from 'lucide-react';
import { Avatar } from './ui/avatar';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { motion, AnimatePresence } from 'framer-motion';
import { articles, rassegne, sources, reports, tierLabels } from '../lib/data';
const navSections = [{
  title: null,
  items: [{
    icon: Home,
    label: 'Panoramica',
    path: '/home'
  }, {
    icon: Newspaper,
    label: 'Rassegne',
    path: '/rassegne'
  }, {
    icon: BarChart3,
    label: 'Statistiche',
    path: '/statistics'
  }, {
    icon: FileText,
    label: 'Report',
    path: '/reports'
  }, {
    icon: TrendingUp,
    label: 'Analisi predittiva',
    path: '/predictive'
  }, {
    icon: BookOpen,
    label: 'Definizioni',
    path: '/definitions'
  }]
}, {
  title: 'CONFIGURAZIONE',
  items: [{
    icon: SlidersHorizontal,
    label: 'Algoritmo',
    path: '/settings/algorithm'
  }, {
    icon: Cable,
    label: 'Connettori',
    path: '/settings/connectors'
  }, {
    icon: Layers,
    label: 'Gestione Tier',
    path: '/settings/tiers'
  }]
}];
function MobileSearchOverlay({
  onClose
}: {
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  const lowerQuery = query.toLowerCase();
  const isSearching = query.trim().length > 0;
  const filteredArticles = isSearching ? articles.filter(a => a.title.toLowerCase().includes(lowerQuery) || a.source.toLowerCase().includes(lowerQuery) || a.author.toLowerCase().includes(lowerQuery)) : articles.slice(0, 3);
  const filteredRassegne = isSearching ? rassegne.filter(r => r.name.toLowerCase().includes(lowerQuery) || r.provider.toLowerCase().includes(lowerQuery)) : rassegne.slice(0, 3);
  const filteredSources = isSearching ? sources.filter(s => s.name.toLowerCase().includes(lowerQuery)) : sources.slice(0, 3);
  const filteredReports = isSearching ? reports.filter(r => r.name.toLowerCase().includes(lowerQuery)) : reports.slice(0, 3);
  const hasResults = filteredArticles.length > 0 || filteredRassegne.length > 0 || filteredSources.length > 0 || filteredReports.length > 0;
  return <motion.div key="mobile-search-overlay" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: 'easeInOut'
  }} className="fixed inset-0 bg-white flex flex-col" style={{
    zIndex: 99999
  }} data-id="element-167">
      {/* Top bar */}
      <div className="h-14 border-b border-border flex items-center justify-between px-4 flex-shrink-0 bg-white" data-id="element-168">
        <button type="button" onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors" aria-label="Chiudi Ricerca" data-id="element-169">
          <X className="h-5 w-5 text-foreground" data-id="element-170" />
        </button>
        <span className="font-medium text-sm text-foreground" data-id="element-171">Ricerca</span>
        <div className="w-9" data-id="element-172" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pt-6 pb-24" data-id="element-173">
        {/* Search Input */}
        <div className="relative mb-8" data-id="element-174">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" data-id="element-175" />
          <input type="text" autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca articoli, rassegne, testate..." className="w-full pl-12 pr-4 py-4 text-base border border-border rounded-2xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent" data-id="element-176" />
        </div>

        {!hasResults ? <div className="text-center py-12" data-id="element-177">
            <Search className="h-7 w-7 text-muted-foreground mx-auto mb-3 opacity-50" data-id="element-178" />
            <p className="text-sm text-muted-foreground" data-id="element-179">
              Nessun risultato per "{query}"
            </p>
          </div> : <div className="space-y-8" data-id="element-180">
            {!isSearching && <div className="text-sm font-medium text-muted-foreground" data-id="element-181">
                Suggerimenti
              </div>}

            {/* Articoli */}
            {filteredArticles.length > 0 && <section data-id="element-182">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-183">
                  <FileText className="h-3.5 w-3.5" data-id="element-184" />
                  Articoli
                </div>
                <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-185">
                  {filteredArticles.map(article => <button key={article.id} onClick={() => {
              onClose();
              navigate(`/article/${article.id}`);
            }} className="w-full text-left px-4 py-3.5 hover:bg-muted/30 transition-colors" data-id="element-186">
                      <div className="text-sm font-medium text-foreground line-clamp-2 mb-1" data-id="element-187">
                        {article.title}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2" data-id="element-188">
                        <span data-id="element-189">{article.source}</span>
                        <span data-id="element-190">·</span>
                        <span data-id="element-191">{article.relevanceScore.toFixed(1)}</span>
                      </div>
                    </button>)}
                </div>
              </section>}

            {/* Rassegne */}
            {filteredRassegne.length > 0 && <section data-id="element-192">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-193">
                  <Newspaper className="h-3.5 w-3.5" data-id="element-194" />
                  Rassegne
                </div>
                <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-195">
                  {filteredRassegne.map(r => <button key={r.id} onClick={() => {
              onClose();
              navigate(`/rassegne/${r.id}`);
            }} className="w-full text-left px-4 py-3.5 hover:bg-muted/30 transition-colors" data-id="element-196">
                      <div className="text-sm font-medium text-foreground line-clamp-1 mb-1" data-id="element-197">
                        {r.name}
                      </div>
                      <div className="text-xs text-muted-foreground" data-id="element-198">
                        {r.dateRange} · {r.articleCount} articoli
                      </div>
                    </button>)}
                </div>
              </section>}

            {/* Report */}
            {filteredReports.length > 0 && <section data-id="element-199">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-200">
                  <LayoutTemplate className="h-3.5 w-3.5" data-id="element-201" />
                  Report
                </div>
                <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-202">
                  {filteredReports.map(r => <button key={r.id} onClick={() => {
              onClose();
              navigate('/reports');
            }} className="w-full text-left px-4 py-3.5 hover:bg-muted/30 transition-colors flex items-center justify-between" data-id="element-203">
                      <div className="min-w-0 flex-1" data-id="element-204">
                        <div className="text-sm font-medium text-foreground line-clamp-1 mb-1" data-id="element-205">
                          {r.name}
                        </div>
                        <div className="text-xs text-muted-foreground" data-id="element-206">
                          {r.dateRange}
                        </div>
                      </div>
                      <Badge variant="outline" className={`ml-3 flex-shrink-0 text-[10px] ${r.status === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`} data-id="element-207">
                        {r.status === 'completed' ? 'Completato' : 'Bozza'}
                      </Badge>
                    </button>)}
                </div>
              </section>}

            {/* Testate */}
            {filteredSources.length > 0 && <section data-id="element-208">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-209">
                  <Building2 className="h-3.5 w-3.5" data-id="element-210" />
                  Testate
                </div>
                <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-211">
                  {filteredSources.map(s => <div key={s.name} className="px-4 py-3.5 flex items-center justify-between" data-id="element-212">
                      <div className="flex items-center gap-3" data-id="element-213">
                        <img src={s.logo} alt={s.name} className="w-7 h-7 rounded-md" data-id="element-214" />
                        <span className="text-sm font-medium text-foreground" data-id="element-215">
                          {s.name}
                        </span>
                      </div>
                      <Badge className="text-[10px] px-2 py-0.5 font-normal bg-muted text-muted-foreground hover:bg-muted" data-id="element-216">
                        {tierLabels[s.tier] || s.tier}
                      </Badge>
                    </div>)}
                </div>
              </section>}
          </div>}
      </div>
    </motion.div>;
}
export function MobileTopBar() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  return <>
      <div className="md:hidden flex flex-col bg-card border-b border-border w-full flex-shrink-0 z-10" data-id="element-217">
        {/* Top row */}
        <div className="flex items-center justify-between px-3 h-14 gap-2" data-id="element-218">
          <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-219">
            <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => setSidebarOpen(true)} data-id="element-220">
              <Menu className="h-5 w-5" data-id="element-221" />
            </Button>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0" data-id="element-222">
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setSearchOpen(true)} data-id="element-223">
              <Search className="h-5 w-5 text-muted-foreground" data-id="element-224" />
            </Button>
            <Avatar className="h-8 w-8 flex-shrink-0" data-id="element-225">
              <img src="https://cdn.magicpatterns.com/uploads/gMQpnDVF4sUMZFXw5c1pgV/kh4ijivu_400x400.jpg" alt="Marco C." className="w-full h-full object-cover rounded-full" data-id="element-226" />
            </Avatar>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Sheet */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen} data-id="element-227">
        <SheetContent side="left" className="w-[280px] p-0 flex flex-col" style={{
        backgroundColor: '#1AA0DB',
        borderRight: '1px solid rgba(255,255,255,0.18)',
        color: '#FFFFFF'
      }} data-id="element-228">
          <SheetHeader className="p-4 text-left" style={{
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }} data-id="element-229">
            <SheetTitle className="font-logo text-[20px] tracking-wide" style={{
            color: '#FFFFFF'
          }} data-id="element-230">
              UCEI
            </SheetTitle>
          </SheetHeader>

          {/* Navigation */}
          <nav className="flex-1 overflow-auto p-2 space-y-4" data-id="element-231">
            {navSections.map((section, idx) => <div key={idx} data-id="element-232">
                {section.title && <div className="px-3 py-2 text-[11px] uppercase tracking-wider" style={{
              color: 'rgba(255,255,255,0.65)'
            }} data-id="element-233">
                    {section.title}
                  </div>}
                <div className="space-y-0.5" data-id="element-234">
                  {section.items.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                return <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors" style={{
                  color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.85)',
                  backgroundColor: isActive ? 'rgba(255,255,255,0.18)' : 'transparent'
                }} data-id="element-235">
                        <Icon className="h-4 w-4 flex-shrink-0" style={{
                    color: '#FFFFFF'
                  }} data-id="element-236" />
                        <span className="text-sm" data-id="element-237">{item.label}</span>
                      </Link>;
              })}
                </div>
              </div>)}
          </nav>

          <div style={{
          borderTop: '1px solid rgba(255,255,255,0.18)'
        }} data-id="element-238" />

          {/* Logout */}
          <div className="p-4" data-id="element-239">
            <Link to="/" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-3 py-2 rounded-md transition-colors" style={{
            color: '#FFFFFF'
          }} data-id="element-240">
              <LogOut className="h-4 w-4" data-id="element-241" />
              <span className="text-sm" data-id="element-242">Esci</span>
            </Link>
          </div>
        </SheetContent>
      </Sheet>

      {/* Mobile Search Overlay */}
      <AnimatePresence data-id="element-243">
        {searchOpen && <MobileSearchOverlay onClose={() => setSearchOpen(false)} data-id="element-244" />}
      </AnimatePresence>
    </>;
}